import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { eventApiClient } from '@src/api/tenant/event/eventApiClient';
import { Event } from '@src/api/types/event';
import { Pagination } from '@src/api/types/pagination';
import { brandColors } from '@src/assets/colors/brandColors';
import PinIcon from '@src/assets/images/common/icon/PinIcon';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { RECEPTION_STATUS_TEXT } from '@src/enum/receptionStatus';
import { useToast } from '@src/features/toast/context/toastContext';
import { useQueryParams } from '@src/hooks/useQueryParams';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import MoreVertMenu from '@src/ui/elements/table/MoreVertMenu';
import StatusTip from '@src/ui/elements/table/StatusTip';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title.tsx';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

type DeleteModal = {
  selectedId?: number;
  isOpen: boolean;
};

const EventListForm: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [deleteModal, setDeleteModal] = useState<DeleteModal>({
    selectedId: undefined,
    isOpen: false,
  });

  const navigate = useNavigate();
  const toast = useToast();

  const {
    searchValue,
    setSearchValue,
    handleSort,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    handleClearParams,
    getSearchParams,
    sort,
    page,
    keyword,
    direction,
  } = useQueryParams({
    defaultSort: 'published_at',
    defaultDirection: 'desc',
  });

  const loadEvents = async () => {
    const params = getSearchParams();
    setLoading(true);

    const res = await eventApiClient.index(params);
    handleApiResponse(res, (data) => {
      setEvents(data.data);
      setPagination(data.pagination);
    });

    setLoading(false);
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, direction, page, keyword]);

  const handleOpenDeleteModal = (id: number) => {
    setDeleteModal({ selectedId: id, isOpen: true });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ selectedId: undefined, isOpen: false });
  };

  const handleDelete = async (id?: number) => {
    if (!id) {
      return;
    }

    setLoading(true);

    const res = await eventApiClient.delete(id);
    handleApiResponse(res, () => {
      loadEvents();
      handleCloseDeleteModal();
      toast(TOAST_MESSAGE.DELETE);
    });

    setLoading(false);
  };

  return (
    <main>
      <SpinnerOverlay loading={loading} />
      <div>
        <div className='flex flex-col'>
          <div className='flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>イベント一覧</Title>
            </div>
            <div className='w-[250px]'>
              <InputSearch
                value={searchValue}
                defaultValue={keyword || ''}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={() => {
                  setSearchValue('');
                  handleClearParams();
                }}
              />
            </div>
          </div>
        </div>
        <div className='mt-4 flex justify-end'>
          <Button
            size='s'
            onClick={() => navigate(TENANT_URL.EVENTS_CREATE)}
          >
            新規作成
          </Button>
        </div>
        {keyword && (
          <p className='overflow-wrap-anywhere mt-8 text-sm'>
            「{keyword}」で絞り込んだ結果：{pagination?.total}件
          </p>
        )}
        <div className='mt-8 overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[112px]' />
              <col className='w-[24px]' />
              <col className='w-[calc(100%-112px-24px-112px-100px-100px-124px-124px-50px)] min-w-[200px]' />
              <col className='w-[112px]' />
              <col className='w-[112px]' />
              <col className='w-[100px]' />
              <col className='w-[100px]' />
              <col className='w-[124px]' />
              <col className='w-[124px]' />
              <col className='w-[50px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sort === 'published_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('published_at')}
                >
                  公開日
                </Th>
                <Th>{''}</Th>
                <Th>イベント名</Th>
                <Th
                  sortable
                  sortOrder={sort === 'event_start_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('event_start_at')}
                >
                  イベント
                  <br />
                  開始日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'event_end_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('event_end_at')}
                >
                  イベント
                  <br />
                  終了日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'recruitment_start_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('recruitment_start_at')}
                >
                  募集
                  <br />
                  開始日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'recruitment_end_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('recruitment_end_at')}
                >
                  募集
                  <br />
                  終了日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'reception_status' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('reception_status')}
                >
                  受付状況
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'status' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('status')}
                >
                  ステータス
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.length > 0
                ? events.map((event) => (
                    <tr
                      className='[&:nth-child(2n)]:bg-gray-10'
                      key={event.id}
                    >
                      <td>
                        <div className='truncate px-3 py-6'>{event.published_at}</div>
                      </td>
                      <td>
                        <div className='flex items-center justify-center py-6'>
                          {event.is_top_display && (
                            <PinIcon
                              color={brandColors['500']}
                              size={20}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.title}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.event_start_at}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.event_end_at}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.recruitment_start_at}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.recruitment_end_at}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>
                          {event.reception_status !== RECEPTION_STATUS_TEXT.UNKNOWN && (
                            <StatusTip
                              label={String(event.reception_status)}
                              active={event.reception_status !== RECEPTION_STATUS_TEXT.CLOSED}
                              color={
                                event.reception_status === RECEPTION_STATUS_TEXT.RECRUITING
                                  ? 'primary'
                                  : null
                              }
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{event.status}</div>
                      </td>
                      <td>
                        <div className='grid h-full place-items-center'>
                          <MoreVertMenu
                            menuItems={[
                              {
                                label: '編集する',
                                onClick: () => {
                                  navigate(generatePath(TENANT_URL.EVENTS_EDIT, { id: event.id }));
                                },
                              },
                              {
                                label: '削除する',
                                variant: 'warning',
                                onClick: () => handleOpenDeleteModal(event.id),
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!keyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
        </div>
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <ConfirmModal
        title='完全に削除しますか？'
        message='イベントが完全に削除されますがよろしいですか？'
        confirmText='完全に削除'
        cancelText='戻る'
        isOpen={deleteModal.isOpen}
        onConfirm={() => handleDelete(deleteModal.selectedId)}
        onClose={handleCloseDeleteModal}
        isDanger={true}
      />
    </main>
  );
};

export default EventListForm;
